// Outlook Add-In Login page
// This page is loaded in an Office Dialog for OAuth authentication

// Office JS type declaration for add-in dialog communication
declare const Office: {
  context?: {
    ui?: {
      messageParent?: (message: string) => void
    }
  }
} | undefined

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { loginSchema, type LoginInput } from '../../../shared/types'

type Stage = 'login' | '2fa' | 'success' | 'error'

export function OutlookLoginPage() {
  const [stage, setStage] = useState<Stage>('login')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [pendingToken, setPendingToken] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  })

  const [twoFactorCode, setTwoFactorCode] = useState('')

  // Send message to parent window (the add-in)
  const sendMessageToParent = (message: object) => {
    // For Office Dialog API
    if (typeof Office !== 'undefined' && Office.context?.ui?.messageParent) {
      Office.context.ui.messageParent(JSON.stringify(message))
    }
    // Also support postMessage for testing
    if (window.opener) {
      window.opener.postMessage(message, '*')
    }
  }

  const onLoginSubmit = async (data: LoginInput) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch('/api/auth/addin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Anmeldung fehlgeschlagen')
      }

      if (result.requiresTwoFactor) {
        setPendingToken(result.pendingSessionToken)
        setStage('2fa')
      } else if (result.success && result.token) {
        setStage('success')
        sendMessageToParent({
          success: true,
          token: result.token,
          user: result.user,
        })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen')
    } finally {
      setIsLoading(false)
    }
  }

  const onTwoFactorSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!pendingToken || !twoFactorCode) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch('/api/auth/addin/verify-2fa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: pendingToken,
          code: twoFactorCode,
        }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Code ungültig')
      }

      if (result.success && result.token) {
        setStage('success')
        sendMessageToParent({
          success: true,
          token: result.token,
          user: result.user,
        })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verifizierung fehlgeschlagen')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    sendMessageToParent({
      success: false,
      error: 'Abgebrochen',
    })
  }

  // Success state
  if (stage === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Erfolgreich angemeldet!</h2>
          <p className="text-sm text-gray-600">Dieses Fenster wird automatisch geschlossen...</p>
        </div>
      </div>
    )
  }

  // 2FA stage
  if (stage === '2fa') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-emerald-600">JustSell</h1>
              <p className="mt-2 text-sm text-gray-600">
                Zwei-Faktor-Authentifizierung
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <form onSubmit={onTwoFactorSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Authentifizierungscode
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                  className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-center text-2xl tracking-widest shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  placeholder="000000"
                  autoFocus
                />
                <p className="mt-1 text-xs text-gray-500">
                  Geben Sie den Code aus Ihrer Authenticator-App ein
                </p>
              </div>

              <button
                type="submit"
                disabled={isLoading || twoFactorCode.length !== 6}
                className="w-full py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? 'Wird überprüft...' : 'Bestätigen'}
              </button>

              <button
                type="button"
                onClick={handleCancel}
                className="w-full py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Abbrechen
              </button>
            </form>
          </div>
        </div>
      </div>
    )
  }

  // Login stage
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-emerald-600">JustSell</h1>
            <p className="mt-2 text-sm text-gray-600">
              Melden Sie sich an
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onLoginSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                E-Mail-Adresse
              </label>
              <input
                {...register('email')}
                type="email"
                autoComplete="email"
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                placeholder="ihre@email.de"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Passwort
              </label>
              <input
                {...register('password')}
                type="password"
                autoComplete="current-password"
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                placeholder="••••••••"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Wird angemeldet...
                </span>
              ) : (
                'Anmelden'
              )}
            </button>

            <button
              type="button"
              onClick={handleCancel}
              className="w-full py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              Abbrechen
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
